import React from 'react';
import { Calendar } from '../ui/calendar';
import { Clock, Plus, Ban, Check } from 'lucide-react';
import { toast } from 'sonner';
import { fr } from 'date-fns/locale';
import { adminUpdateSlot, adminBulkUpdateSlots, adminGetSlots } from '../../services/api';

const AdminSlotsTab = ({
  slotDate, setSlotDate, customTime, setCustomTime, customLabel, setCustomLabel,
  token, adminSlots, setAdminSlots, TIME_SLOTS,
  blockedSet, bookingsByDatetime, blockEntireDay, openEntireDay, toggleSlotBlock
}) => {
  const formatDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const handleAddCustomSlot = async () => {
    if (!slotDate || !customTime) return;
    const dateStr = formatDateStr(slotDate);
    try {
      await adminUpdateSlot(token, dateStr, customTime, false, customLabel || '');
      setAdminSlots(prev => {
        const filtered = prev.filter(s => !(s.date === dateStr && s.time === customTime));
        filtered.push({ date: dateStr, time: customTime, blocked: false, label: customLabel });
        return filtered;
      });
      toast.success('Créneau ajouté');
      setCustomLabel('');
    } catch {
      toast.error('Erreur');
    }
  };

  return (
          <div data-testid="admin-slots-management">
            <h2 className="heading-3" style={{ marginBottom: '1rem' }}>Gérer vos disponibilités</h2>
            <p className="body-small" style={{ marginBottom: '2rem', color: 'var(--text-muted)' }}>
              Sélectionnez une date dans le calendrier, puis bloquez ou ouvrez les créneaux individuellement ou pour toute la journée.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: '2rem', alignItems: 'start' }}>
              <div className="card" style={{ padding: '1rem' }}>
                <Calendar
                  mode="single"
                  selected={slotDate}
                  onSelect={setSlotDate}
                  locale={fr}
                  className="rounded-md"
                />
              </div>

              {slotDate && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                    <h3 className="heading-3">
                      {slotDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </h3>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="btn-secondary" style={{ padding: '0.4rem 0.75rem', fontSize: '0.7rem' }}
                        onClick={() => blockEntireDay(formatDateStr(slotDate))} data-testid="block-entire-day">
                        <Ban size={14} /> Bloquer la journée
                      </button>
                      <button className="btn-primary" style={{ padding: '0.4rem 0.75rem', fontSize: '0.7rem' }}
                        onClick={() => openEntireDay(formatDateStr(slotDate))} data-testid="open-entire-day">
                        <Check size={14} /> Ouvrir la journée
                      </button>
                    </div>
                  </div>

                  {/* Add custom slot outside default range */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem',
                    padding: '0.75rem 1rem', borderRadius: '12px',
                    background: '#fffbeb', border: '2px dashed #f59e0b'
                  }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#92400e', whiteSpace: 'nowrap' }}>Ajouter un créneau :</span>
                    <input
                      type="time"
                      value={customTime}
                      onChange={(e) => setCustomTime(e.target.value)}
                      style={{ padding: '0.4rem 0.5rem', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.85rem' }}
                      data-testid="custom-time-input"
                    />
                    <input
                      type="text"
                      value={customLabel}
                      onChange={(e) => setCustomLabel(e.target.value)}
                      placeholder="Label (ex: Urgence VAE)"
                      style={{ padding: '0.4rem 0.5rem', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.8rem', flex: 1 }}
                      data-testid="custom-label-input"
                    />
                    <button
                      data-testid="add-custom-slot-btn"
                      className="btn-primary"
                      style={{ padding: '0.4rem 0.75rem', fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                      onClick={async () => {
                        if (!customTime) return;
                        try {
                          await adminUpdateSlot(token, formatDateStr(slotDate), customTime, false, customLabel || '');
                          toast.success(`Créneau ${customTime} ouvert !`);
                          const fresh = await adminGetSlots(token);
                          setAdminSlots(fresh);
                          setCustomLabel('');
                        } catch { toast.error('Erreur lors de l\'ajout'); }
                      }}
                    >
                      <Check size={14} /> Ouvrir
                    </button>
                  </div>

                  {/* Day type indicator */}
                  {slotDate.getDay() === 0 && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '10px', padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#dc2626', fontWeight: 600 }}>
                      Dimanche — Tous les créneaux sont bloqués par défaut. Ouvrez ceux dont vous avez besoin.
                    </div>
                  )}
                  {slotDate.getDay() === 6 && (
                    <div style={{ background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '10px', padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#92400e', fontWeight: 600 }}>
                      Samedi — Créneaux après 12h bloqués par défaut. Ouvrez-les si besoin.
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {TIME_SLOTS.map(time => {
                      const dateStr = formatDateStr(slotDate);
                      const key = `${dateStr}|${time}`;
                      const explicitlyBlocked = blockedSet.has(key);
                      const hasBooking = (bookingsByDatetime[key] || []).length > 0;

                      // Check if admin explicitly opened this slot
                      const explicitlyOpened = adminSlots.some(s => s.date === dateStr && s.time === time && !s.blocked);

                      // Default blocked rules:
                      // Sunday: all slots blocked by default
                      // Saturday: slots >= 12:00 blocked by default
                      const isSunday = slotDate.getDay() === 0;
                      const isSaturday = slotDate.getDay() === 6;
                      const hour = parseInt(time.split(':')[0], 10);
                      const defaultBlocked = isSunday || (isSaturday && hour >= 12);

                      // Final blocked state: explicit admin action overrides default
                      const isBlocked = explicitlyBlocked || (defaultBlocked && !explicitlyOpened);

                      return (
                        <div key={time} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '1rem 1.25rem', borderRadius: '12px',
                          background: hasBooking ? '#f0fdf4' : isBlocked ? '#fef2f2' : 'var(--bg-card)',
                          border: `2px solid ${hasBooking ? '#86efac' : isBlocked ? '#fca5a5' : 'var(--border-light)'}`
                        }} data-testid={`slot-${dateStr}-${time}`}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <Clock size={20} style={{ color: 'var(--text-muted)' }} />
                            <span style={{ fontWeight: 600, fontSize: '1rem' }}>{time}</span>
                            {hasBooking && (
                              <span style={{ background: '#dcfce7', color: '#166534', padding: '0.2rem 0.6rem', borderRadius: '1rem', fontSize: '0.7rem', fontWeight: 600 }}>
                                Réservé — {(bookingsByDatetime[key] || [])[0]?.candidateInfo?.prenom || (bookingsByDatetime[key] || [])[0]?.userName || '—'}
                              </span>
                            )}
                            {isBlocked && !hasBooking && (
                              <span style={{ background: '#fee2e2', color: '#dc2626', padding: '0.2rem 0.6rem', borderRadius: '1rem', fontSize: '0.7rem', fontWeight: 600 }}>
                                Bloqué{defaultBlocked && !explicitlyBlocked ? ' (par défaut)' : ''}
                              </span>
                            )}
                            {!isBlocked && !hasBooking && (
                              <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '0.2rem 0.6rem', borderRadius: '1rem', fontSize: '0.7rem', fontWeight: 600 }}>
                                Disponible
                              </span>
                            )}
                          </div>
                          {!hasBooking && (
                            <button
                              onClick={() => toggleSlotBlock(dateStr, time)}
                              style={{
                                padding: '0.4rem 0.75rem', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600,
                                cursor: 'pointer', border: 'none',
                                background: isBlocked ? '#dcfce7' : '#fee2e2',
                                color: isBlocked ? '#166534' : '#dc2626'
                              }}
                              data-testid={`toggle-slot-${time}`}>
                              {isBlocked ? 'Ouvrir' : 'Bloquer'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!slotDate && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px', color: 'var(--text-muted)' }}>
                  <p className="body-small">Sélectionnez une date pour gérer les créneaux</p>
                </div>
              )}
            </div>
          </div>

  );
};

export default AdminSlotsTab;
